import type { ReactNode, Ref } from 'react';
import { Link } from 'react-router';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { MOTION, type MotionState } from '../motion';

/**
 * A DASHBOARD ROW: the SEVERITY ROW and the DENSE POOLED ROW, which are one component
 * because they are one shape with two decorations (dashboard.md D24, D26, D28).
 *
 * DENSER THAN THE TABLE, ON PURPOSE. `--row-height-dense` is 44px against the table's
 * 54: a panel row's job is to fit the day, a table row's is to be a comfortable editing
 * target. 44px is `--touch-target` exactly, so nothing crosses a floor. DESIGN.md allows
 * Attio as a reference for data-density patterns and nothing else, and this is that
 * reference's one legitimate use on this screen.
 *
 * THE RAIL: a 3px bar on the leading edge, `danger`, `warning`, `success` or `neutral`. It
 * REUSES `--row-rail-width`, whose own comment says "accent rail on the row whose cell has
 * focus" - a semantic overload, named rather than discovered. The two never share a screen,
 * so the width is shared and the colour is what separates them; the day they meet, the fix
 * is a second token and not a second meaning for this one.
 *
 * HOME NO LONGER ASKS FOR IT (wave 3 round 2, ledger P3-W3-9). The attention row drew the
 * bar AND a tinted capsule for the same health value, which the owner rejected on
 * 2026-09-10 as a web pattern - "run cards with coloured left borders and status pills" -
 * and a list row in Apple's own lists carries nothing along its edge (hig:
 * https://developer.apple.com/design/human-interface-guidelines/lists-and-tables). That row
 * states its colour once now, as the dot `SelectDisplay` draws in front of the option's
 * word, which is HIG Color's pairing rule ("Don't rely on color alone") satisfied exactly
 * once rather than twice.
 *
 * AND NEITHER DOES THE WORKFLOW RUN LIST, since the wave-3 legacy round (2026-09-11). That
 * was the one caller this paragraph named as still asking for the bar, and it is a settings
 * pane's grouped list now: the status is one word with the same dot in front of it, drawn by
 * the same `fields/display/TagDot`. `/dev/kitchen-sink` is the prop's only remaining caller,
 * so the rail is a piece the gallery documents and no screen draws - which is the moment to
 * delete it, in a round that owns this file.
 */
/**
 * TWO VARIANTS ARRIVED WITH THE WORKFLOW BUILDER (workflow-builder.md D42, red-team M3).
 *
 * A run list has four resting states, not two: succeeded, failed, stopped-by-a-guard and
 * "nothing to say yet". `success` and `neutral` map to `--color-success` and `--color-line`,
 * both of which already exist - so "zero new COLOUR tokens" survives, which "zero new
 * anything" did not. `--row-rail-width` is unchanged.
 */
export type RowRail = 'danger' | 'warning' | 'success' | 'neutral' | 'none';

const RAILS: Record<RowRail, string> = {
  none: '',
  danger: 'before:bg-danger',
  warning: 'before:bg-warning',
  success: 'before:bg-success',
  neutral: 'before:bg-line',
};

/**
 * THE ROW ASKS ITS CONTAINER FOR A GUTTER, and the contract is worth stating because
 * breaking it is invisible until somebody hovers: the link below is `-mx-2 px-2`, so its
 * hover fill is 8px wider than the text column on EACH side. A list that gives it less
 * than `--space-2` of horizontal padding paints that fill over its own edge - which is
 * what a `Card padded={false}` plus a one-sided `pl-2` did on the inbound source list
 * (owner's click, 2026-09-07: "the hover runs past the card"). A padded `Card` already
 * clears it at 16px; an unpadded one needs `px-2` on the list, never `pl-2`.
 */
export interface DashboardRowProps {
  /** Where the row goes. Omit for a row whose only affordance is inside it. */
  readonly to?: string | undefined;
  readonly rail?: RowRail | undefined;
  /** Rendered before the title, outside the link when there is one (a checkbox). */
  readonly leading?: ReactNode | undefined;
  readonly title: ReactNode;
  /** Right-aligned on the title line: a chip, a due badge, an age. */
  readonly trailing?: ReactNode | undefined;
  /**
   * INTERACTIVE controls, rendered OUTSIDE the link (workflow-builder.md M3).
   *
   * `trailing` sits INSIDE `body`, which is wrapped in a `<Link>` whenever `to` is set - so
   * a `Switch` or a `DropdownMenu` put there is a `<button>` nested in an `<a>`: invalid
   * HTML, a keyboard and screen-reader failure, and a tap that both toggles and navigates.
   * The prop's own comment says what `trailing` is for ("a chip, a due badge, an age"), and
   * `leading` is already rendered outside the link - this is the same move on the other
   * side.
   */
  readonly actions?: ReactNode | undefined;
  /** One muted line under the title. Truncated rather than wrapped. */
  readonly secondary?: ReactNode | undefined;
  /** A third line, for the run a score belongs to. */
  readonly tertiary?: ReactNode | undefined;
  /** A completed task: the row recedes rather than disappearing under the finger. */
  readonly muted?: boolean | undefined;
  readonly className?: string | undefined;
  readonly rowRef?: Ref<HTMLLIElement> | undefined;
  readonly motionState?: MotionState | undefined;
  /* --- fe-foundation (redesign, 2026-10-04) --- the redesigned list row of
     dashboard-v2-side.html:33-43: status symbol | name | owner | days | chevron, a second line
     under the name, and row actions at the right of that second line. Every prop below is
     optional; a row that sets none of them renders exactly as before (register A6). */
  /** Before the name, inside the link: a `StatusSymbol` (decorative, its word is the line). */
  readonly status?: ReactNode | undefined;
  /** A fixed owner column after the name, in the secondary ink (a first name). */
  readonly owner?: ReactNode | undefined;
  /** A right-aligned days column ("80 days"), tabular. */
  readonly days?: ReactNode | undefined;
  /** The trailing `chevron-right` (rest-of-CRM audit H11): the row opens a page. */
  readonly chevron?: boolean | undefined;
}

export function DashboardRow({
  to,
  rail = 'none',
  leading,
  title,
  trailing,
  actions,
  secondary,
  tertiary,
  muted = false,
  className,
  rowRef,
  motionState,
  status,
  owner,
  days,
  chevron = false,
}: DashboardRowProps) {
  const columns = status !== undefined || owner !== undefined || days !== undefined || chevron;
  if (columns) {
    return (
      <ColumnRow
        to={to}
        leading={leading}
        title={title}
        trailing={trailing}
        actions={actions}
        secondary={secondary}
        tertiary={tertiary}
        muted={muted}
        className={className}
        rowRef={rowRef}
        motionState={motionState}
        status={status}
        owner={owner}
        days={days}
        chevron={chevron}
      />
    );
  }
  const body = (
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
      <div className="flex min-w-0 items-baseline gap-2">
        <span
          className={cx('min-w-0 flex-1 truncate text-sm', muted ? 'text-ink-subtle' : 'text-ink')}
        >
          {title}
        </span>
        {trailing !== undefined && <span className="shrink-0">{trailing}</span>}
      </div>
      {secondary !== undefined && (
        <span className="truncate text-2xs text-ink-muted">{secondary}</span>
      )}
      {tertiary !== undefined && (
        <span className="truncate text-2xs text-ink-subtle">{tertiary}</span>
      )}
    </div>
  );

  return (
    <li
      ref={rowRef}
      data-state={motionState}
      inert={motionState === 'closed'}
      aria-hidden={motionState === 'closed' ? true : undefined}
      className={cx(
        'relative flex min-h-(--row-height-dense) items-stretch gap-3 py-2',
        // The rail is a pseudo-element so it spans the row's full height whatever the
        // content does, and costs no wrapper element per row.
        rail !== 'none' &&
          'before:absolute before:inset-y-1 before:-left-2 before:w-(--row-rail-width) before:rounded-pill before:content-[""]',
        RAILS[rail],
        MOTION.state,
        muted && 'opacity-60',
        className,
      )}
    >
      {leading !== undefined && <div className="flex shrink-0 items-center">{leading}</div>}
      {to === undefined ? (
        body
      ) : (
        /**
         * `.pressable` and NOT a press scale: this is a card row, which is on M2's own
         * coverage list, and the asymmetry it buys is the point - the surface lands on
         * the frame the finger did and only the release eases.
         */
        <Link
          to={to}
          /**
           * THE LINK CARRIES THE TOUCH FLOOR, NOT THE ROW (E5). The `<li>` is
           * `--row-height-dense` and its padding eats into that, so the thing a finger
           * actually presses measured 43px inside a 44px row - a floor kept by the
           * wrapper is a floor the target does not have. Costs one pixel of row height
           * on a phone and nothing on desktop, where the floor is 40.
           */
          className="pressable -mx-2 flex min-h-(--touch-target-min) min-w-0 flex-1 items-center rounded-control px-2 hover:bg-surface-hover"
        >
          {body}
        </Link>
      )}
      {actions !== undefined && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
    </li>
  );
}

/**
 * THE REDESIGNED LIST ROW (fe-foundation; dashboard-v2-side.html:33-43, components page
 * section 2 "Mark as handled on a Waiting row"). One grid: the status symbol, the name with
 * the second line under it, the owner column, the days column at the trailing edge and the
 * chevron; the row's actions ("Mark as handled", "Pause…") at the right of the second line,
 * under the owner and days columns, visible at rest (D2, L11). The two text actions together
 * are a few pixels wider than those two columns; they never wrap or shrink and take the
 * column gap on their leading side instead.
 *
 * THE LINK IS STRETCHED, NOT WRAPPED. The client's name is the link and its `::after` covers
 * the whole row, so a click anywhere opens the client, while the second line and the actions
 * sit in the row itself rather than inside an `<a>`: a button never sits inside a link, and
 * the second line may hold one - a quote clamped by `ExpandableText` draws its own "More"
 * under the text. A control in the row paints above the stretched layer because it comes
 * later in the document and is positioned.
 *
 * No underline on hover (open question Q-CM-1, decided A: the build-slice checklist), the
 * product's row hover fill on the whole row, the arrow cursor, and the keyboard ring on the
 * row (`.focus-ring-within`, which the pointer modality turns off after a click).
 */
function ColumnRow({
  to,
  leading,
  title,
  trailing,
  actions,
  secondary,
  tertiary,
  muted = false,
  className,
  rowRef,
  motionState,
  status,
  owner,
  days,
  chevron = false,
}: DashboardRowProps) {
  return (
    <li
      ref={rowRef}
      data-state={motionState}
      inert={motionState === 'closed'}
      aria-hidden={motionState === 'closed' ? true : undefined}
      data-row-columns=""
      className={cx(
        'relative -mx-2 flex min-h-(--row-height-dense) items-start gap-3 rounded-control px-2 py-2',
        to !== undefined && 'focus-ring-within cursor-default hover:bg-surface-hover',
        MOTION.state,
        muted && 'opacity-60',
        className,
      )}
    >
      {leading !== undefined && <div className="flex shrink-0 items-start pt-0.5">{leading}</div>}
      <div className="grid min-w-0 flex-1 grid-cols-[auto_minmax(0,1fr)_var(--row-owner-width)_var(--row-days-width)_auto] items-start gap-x-3 gap-y-0.5">
        <span className="col-start-1 row-start-1 flex min-h-5 items-center text-sm">{status}</span>
        <span
          className={cx(
            'col-start-2 row-start-1 min-w-0 text-sm [overflow-wrap:anywhere]',
            muted ? 'text-ink-subtle' : 'text-ink',
          )}
        >
          {to === undefined ? (
            title
          ) : (
            <Link
              to={to}
              className={cx(
                'cursor-default outline-none',
                "after:absolute after:inset-0 after:rounded-[inherit] after:content-['']",
                /* The pressable box is the whole row; the measure reads its floor here. */
                'after:min-h-(--touch-target-min) after:min-w-(--touch-target-min)',
              )}
            >
              {title}
            </Link>
          )}
          {trailing !== undefined && <span className="ml-2">{trailing}</span>}
        </span>
        {owner !== undefined && (
          <span className="col-start-3 row-start-1 text-2xs leading-5 whitespace-nowrap text-ink-muted">
            {owner}
          </span>
        )}
        {days !== undefined && (
          <span className="col-start-4 row-start-1 text-right text-sm font-semibold whitespace-nowrap tabular-nums">
            {days}
          </span>
        )}
        {chevron && (
          <Icon
            name="chevron-right"
            className="col-start-5 row-span-2 row-start-1 self-center text-ink-subtle"
            size="0.875rem"
          />
        )}
        {secondary !== undefined && (
          <div className="col-start-2 row-start-2 min-w-0 text-2xs text-ink-muted">{secondary}</div>
        )}
        {actions !== undefined && (
          <div
            className="relative col-span-2 col-start-3 row-start-2 flex items-center justify-end gap-1.5 self-end [&>*]:shrink-0"
            data-row-actions=""
          >
            {actions}
          </div>
        )}
        {tertiary !== undefined && (
          <span className="col-start-2 row-start-3 min-w-0 text-2xs text-ink-subtle">
            {tertiary}
          </span>
        )}
      </div>
    </li>
  );
}
